export interface AutoResetTargetPresentationDecision {
  readonly targetStructureAction:
    | "commit-snapshot"
    | "hold-last-presentation-worthy";
  readonly updateTargetThemeHue: boolean;
  readonly scheduleHoverRefreshAfterStructureCommit: boolean;
}

export type AutoResetTargetPresentationSource = "mhov" | "nhov" | "ohov";

export interface AutoResetTargetPresentationGate {
  readonly beginFrame: () => void;
  readonly markAutoResetExecuted: (
    source: AutoResetTargetPresentationSource,
  ) => void;
  readonly resolveUpdate: (options?: {
    readonly isStableFrameCommit?: boolean;
    readonly hasPresentationWorthyTargetStructure?: boolean;
    readonly suppressTargetStructureCommit?: boolean;
  }) => AutoResetTargetPresentationDecision;
  readonly completeUpdate: (
    decision: AutoResetTargetPresentationDecision,
  ) => void;
}

export function createAutoResetTargetPresentationGate(): AutoResetTargetPresentationGate {
  let autoResetExecutedInFrame = false;
  let stableTargetCommitPending = false;
  let stableTargetCommitInProgress = false;

  const holdLastPresentationWorthyStructure =
    (): AutoResetTargetPresentationDecision => ({
      targetStructureAction: "hold-last-presentation-worthy",
      updateTargetThemeHue: true,
      scheduleHoverRefreshAfterStructureCommit: false,
    });

  const commitSnapshot = (
    scheduleHoverRefreshAfterStructureCommit: boolean,
  ): AutoResetTargetPresentationDecision => ({
    targetStructureAction: "commit-snapshot",
    updateTargetThemeHue: true,
    scheduleHoverRefreshAfterStructureCommit,
  });

  return {
    beginFrame(): void {
      autoResetExecutedInFrame = false;
    },

    markAutoResetExecuted(_source): void {
      autoResetExecutedInFrame = true;
      stableTargetCommitPending = true;
    },

    resolveUpdate(options = {}): AutoResetTargetPresentationDecision {
      if (
        autoResetExecutedInFrame ||
        stableTargetCommitInProgress ||
        options.suppressTargetStructureCommit === true
      ) {
        return holdLastPresentationWorthyStructure();
      }

      if (!stableTargetCommitPending) {
        return commitSnapshot(false);
      }

      // Reaching here means the frame carries no same-frame auto reset, no
      // in-flight commit, and no external suppression, so the post-reset hold
      // can resolve. Releasing on either recovery condition keeps the
      // continuous-reset flicker guard (the early hold above) while avoiding an
      // unbounded hold when growth settles at tier 1.
      const restoredPresentationWorthyStructure =
        options.isStableFrameCommit === true &&
        options.hasPresentationWorthyTargetStructure === true;
      const tier1TargetSettled =
        options.hasPresentationWorthyTargetStructure !== true;

      if (!restoredPresentationWorthyStructure && !tier1TargetSettled) {
        return holdLastPresentationWorthyStructure();
      }

      stableTargetCommitPending = false;
      stableTargetCommitInProgress = true;
      return commitSnapshot(true);
    },

    completeUpdate(decision): void {
      if (decision.scheduleHoverRefreshAfterStructureCommit) {
        stableTargetCommitInProgress = false;
      }
    },
  };
}
